import { Button } from "@trotroways/ui";
import { cn, formatEventDuration } from "@trotroways/utils";
import {
	ArrowLeft,
	Calendar,
	Clock,
	ExternalLink,
	Globe,
	Heart,
	Mail,
	MapPin,
	Navigation,
	Phone,
	Share2,
	Ticket,
	Users,
	X,
} from "lucide-react";
import { usePostHog } from "@/lib/posthog-client";
import { memo, useCallback, useState } from "react";
import { EventTypeBadge } from "@/components/map-ui/event-type-badge";
import { type Event, getEventColor } from "@/lib/api";

interface EventDetailPanelProps {
	event: Event;
	onBack: () => void;
	onGetDirections?: (event: Event) => void;
	onClose?: () => void;
}

function formatEventDateTime(
	startTime: string,
	endTime: string,
	allDay?: boolean | null,
): string {
	const start = new Date(startTime);
	const end = new Date(endTime);

	const dateOptions: Intl.DateTimeFormatOptions = {
		weekday: "short",
		month: "short",
		day: "numeric",
	};

	const timeOptions: Intl.DateTimeFormatOptions = {
		hour: "numeric",
		minute: "2-digit",
		hour12: true,
	};

	const dateStr = start.toLocaleDateString("en-US", dateOptions);

	if (allDay) {
		return `${dateStr} (All Day)`;
	}

	const startTimeStr = start.toLocaleTimeString("en-US", timeOptions);
	const endTimeStr = end.toLocaleTimeString("en-US", timeOptions);

	return `${dateStr}, ${startTimeStr} - ${endTimeStr}`;
}

export const EventDetailPanel = memo(function EventDetailPanel({
	event,
	onBack,
	onGetDirections,
	onClose,
}: EventDetailPanelProps) {
	const posthog = usePostHog();
	const [imageLoaded, setImageLoaded] = useState(false);
	const [isLiked, setIsLiked] = useState(false);
	const eventColor = getEventColor(event.event_type);
	const hasImage = !!event.cover_image_url;
	const allImages = [
		...(event.cover_image_url ? [event.cover_image_url] : []),
		...(event.images || []),
	].filter((v, i, a) => a.indexOf(v) === i);

	const handleImageLoad = useCallback(() => setImageLoaded(true), []);
	const toggleLike = useCallback(() => setIsLiked((prev) => !prev), []);

	const handleShare = useCallback(async () => {
		if (navigator.share) {
			try {
				await navigator.share({
					title: event.title,
					text: event.description || `Check out ${event.title}`,
					url: window.location.href,
				});
				posthog.capture("event_shared", {
					event_id: event.id,
					event_title: event.title,
					event_type: event.event_type,
				});
			} catch (err) {
				console.error("Share failed:", err);
			}
		}
	}, [event, posthog]);

	const handleGetDirections = useCallback(() => {
		posthog.capture("event_directions_requested", {
			event_id: event.id,
			event_title: event.title,
			event_type: event.event_type,
			venue_name: event.venue_name,
		});
		onGetDirections?.(event);
	}, [event, onGetDirections, posthog]);

	return (
		<div className="flex flex-col h-full max-h-none md:max-h-[calc(100vh-8rem)] overflow-hidden">
			<div className="flex items-center justify-between py-2 border-b border-gray-100">
				<Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5">
					<ArrowLeft className="w-4 h-4" />
					Back
				</Button>
				<div className="flex items-center gap-2">
					<Button
						variant="ghost"
						size="icon"
						onClick={toggleLike}
						className={cn(isLiked && "text-red-500")}
					>
						<Heart className={cn("w-4 h-4", isLiked && "fill-current")} />
					</Button>
					<Button variant="ghost" size="icon" onClick={handleShare}>
						<Share2 className="w-4 h-4" />
					</Button>
					{onClose && (
						<Button variant="ghost" size="icon" onClick={onClose}>
							<X className="w-4 h-4" />
						</Button>
					)}
				</div>
			</div>

			<div className="flex-1 overflow-y-auto">
				<div className="relative aspect-video bg-gray-100 overflow-hidden rounded-md">
					{hasImage ? (
						<>
							<img
								src={event.cover_image_url ?? ""}
								alt={event.title}
								className={cn(
									"w-full h-full object-cover transition-opacity duration-300",
									imageLoaded ? "opacity-100" : "opacity-0",
								)}
								onLoad={handleImageLoad}
							/>
							{!imageLoaded && (
								<div
									className="absolute inset-0 animate-pulse"
									style={{
										backgroundColor: `${eventColor}20`,
									}}
								/>
							)}
						</>
					) : (
						<div
							className="w-full h-full flex items-center justify-center"
							style={{ backgroundColor: `${eventColor}15` }}
						>
							<Calendar className="w-16 h-16" style={{ color: eventColor }} />
						</div>
					)}

					<div className="absolute inset-0 bg-linear-to-t from-black/60 via-transparent to-transparent" />

					<div className="absolute bottom-4 left-4 right-4">
						<EventTypeBadge eventType={event.event_type} className="mb-2" />
						<h2 className="text-xl font-bold text-white leading-tight">
							{event.title}
						</h2>
					</div>

					{allImages.length > 1 && (
						<div className="absolute bottom-4 right-4 flex gap-1">
							{allImages.slice(1, 4).map((img, idx) => (
								<div
									key={idx}
									className="w-10 h-10 rounded-lg border-2 border-white shadow-sm overflow-hidden"
								>
									<img
										src={img}
										alt=""
										className="w-full h-full object-cover"
										loading="lazy"
									/>
								</div>
							))}
							{allImages.length > 4 && (
								<div className="w-10 h-10 rounded-lg border-2 border-white bg-black/60 flex items-center justify-center">
									<span className="text-xs text-white font-medium">
										+{allImages.length - 4}
									</span>
								</div>
							)}
						</div>
					)}
				</div>

				<div className="py-2 space-y-4">
					<div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
						<div
							className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
							style={{ backgroundColor: `${eventColor}15` }}
						>
							<Calendar className="w-5 h-5" style={{ color: eventColor }} />
						</div>
						<div>
							<p className="font-medium text-gray-900 text-sm">
								{formatEventDateTime(
									event.start_time,
									event.end_time,
									event.all_day,
								)}
							</p>
							<div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
								<Clock className="w-3 h-3" />
								<span>
									{formatEventDuration(event.start_time, event.end_time)}
								</span>
							</div>
						</div>
					</div>

					{(event.venue_name || event.address) && (
						<div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
							<div
								className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
								style={{ backgroundColor: `${eventColor}15` }}
							>
								<MapPin className="w-5 h-5" style={{ color: eventColor }} />
							</div>
							<div className="flex-1 min-w-0">
								{event.venue_name && (
									<p className="font-medium text-gray-900 text-sm">
										{event.venue_name}
									</p>
								)}
								{event.address && (
									<p className="text-xs text-gray-500 mt-0.5 truncate">
										{event.address}
									</p>
								)}
							</div>
						</div>
					)}

					{event.price_info && (
						<div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
							<div
								className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
								style={{ backgroundColor: `${eventColor}15` }}
							>
								<Ticket className="w-5 h-5" style={{ color: eventColor }} />
							</div>
							<div>
								<p className="font-medium text-gray-900 text-sm">
									{event.price_info}
								</p>
								<p className="text-xs text-gray-500 mt-0.5">Entry fee</p>
							</div>
						</div>
					)}

					{(event.attendee_count ?? 0) > 0 && (
						<div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
							<div className="flex -space-x-2">
								{Array.from({
									length: Math.min(event.attendee_count || 0, 5),
								}).map((_, idx) => (
									<div
										key={idx}
										className="w-8 h-8 rounded-full border-2 border-white flex items-center justify-center"
										style={{
											backgroundColor: `hsl(${(idx * 60 + 200) % 360}, 70%, 85%)`,
											zIndex: 5 - idx,
										}}
									>
										<Users className="w-3.5 h-3.5 text-gray-600" />
									</div>
								))}
							</div>
							<div className="flex-1">
								<p className="font-medium text-gray-900 text-sm">
									{event.attendee_count}{" "}
									{event.attendee_count === 1 ? "person" : "people"} going
								</p>
								{event.capacity && (
									<p className="text-xs text-gray-500 mt-0.5">
										{event.capacity - (event.attendee_count || 0)} spots left
									</p>
								)}
							</div>
						</div>
					)}

					{event.description && (
						<div className="space-y-2">
							<h3 className="font-semibold text-gray-900 text-sm">About</h3>
							<p className="text-sm text-gray-600 leading-relaxed">
								{event.description}
							</p>
						</div>
					)}

					{event.organizer && (
						<div className="space-y-2">
							<h3 className="font-semibold text-gray-900 text-sm">Organizer</h3>
							<p className="text-sm text-gray-600">{event.organizer}</p>
						</div>
					)}

					{event.contact_info && (
						<div className="space-y-2">
							<h3 className="font-semibold text-gray-900 text-sm">Contact</h3>
							<div className="space-y-2">
								{event.contact_info.phone && (
									<a
										href={`tel:${event.contact_info.phone}`}
										className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
									>
										<Phone className="w-4 h-4" />
										{event.contact_info.phone}
									</a>
								)}
								{event.contact_info.email && (
									<a
										href={`mailto:${event.contact_info.email}`}
										className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
									>
										<Mail className="w-4 h-4" />
										{event.contact_info.email}
									</a>
								)}
								{event.contact_info.website && (
									<a
										href={event.contact_info.website}
										target="_blank"
										rel="noopener noreferrer"
										className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
									>
										<Globe className="w-4 h-4" />
										Visit website
										<ExternalLink className="w-3 h-3" />
									</a>
								)}
							</div>
						</div>
					)}
				</div>
			</div>

			<div className="py-4 border-t border-gray-100 bg-white">
				<Button
					className="w-full gap-2"
					style={{ backgroundColor: eventColor }}
					onClick={handleGetDirections}
				>
					<Navigation className="w-4 h-4" />
					Get Directions
				</Button>
			</div>
		</div>
	);
});
